// Shared clinic print utilities — examination record & treatment plan.
// Uses window.open + inline HTML for reliable cross-browser printing.

function esc(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ));
}

const BRAND_STYLES = `
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif;
    margin: 0; padding: 32px; color: #1e293b; background: #fff; }
  .header { display: flex; justify-content: space-between; align-items: flex-start;
    margin-bottom: 20px; padding-bottom: 16px; border-bottom: 2px solid #0F2A5F; }
  .clinic-name { font-size: 22px; font-weight: 800; color: #0F2A5F; margin: 0 0 4px 0; }
  .doctor-name { font-size: 14px; color: #334155; font-weight: 600; margin-bottom: 4px; }
  .clinic-info { font-size: 12px; color: #64748b; line-height: 1.5; }
  .doc-title { font-size: 26px; font-weight: 800; color: #14B8A6; letter-spacing: 1.5px; margin: 0; text-align: right; }
  .doc-date { font-size: 12px; color: #64748b; text-align: right; margin-top: 4px; }
  .patient-box { background: #f1f5f9; border-radius: 6px; padding: 12px 16px; margin-bottom: 20px; }
  .patient-label { font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #64748b; margin-bottom: 4px; }
  .patient-name { font-weight: 700; color: #0F2A5F; font-size: 15px; }
  .patient-info { font-size: 12px; color: #475569; margin-top: 2px; }
  h2 { color: #0F2A5F; font-size: 16px; margin: 24px 0 8px 0; padding-bottom: 6px; border-bottom: 1px solid #e2e8f0; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 12px; font-size: 13px; }
  thead th { background: #0F2A5F; color: white; text-align: left; padding: 8px 10px;
    font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
  thead th.right { text-align: right; }
  tbody td { padding: 8px 10px; border-bottom: 1px solid #e2e8f0; }
  td.right { text-align: right; }
  td.mono { font-family: monospace; font-weight: 600; color: #0F2A5F; }
  .badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: 600;
    background: #E6F7F5; color: #0F8A7D; border: 1px solid #14B8A6; margin-right: 3px; margin-bottom: 2px; }
  .status-badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
  .status-advised { background: #f1f5f9; color: #334155; }
  .status-in-progress { background: #dbeafe; color: #1e40af; }
  .status-completed { background: #dcfce7; color: #166534; }
  .status-cancelled { background: #fee2e2; color: #991b1b; }
  .totals { margin-left: auto; width: 320px; font-size: 13px; margin-top: 12px; }
  .totals .row { display: flex; justify-content: space-between; padding: 5px 0; }
  .totals .row.grand { border-top: 2px solid #0F2A5F; padding-top: 10px; margin-top: 6px;
    font-weight: 700; font-size: 16px; color: #0F2A5F; }
  .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e2e8f0;
    text-align: center; font-size: 11px; color: #94a3b8; }
  .signature { margin-top: 60px; display: flex; justify-content: space-between; font-size: 12px; color: #475569; }
  .signature .line { border-top: 1px solid #334155; padding-top: 4px; width: 200px; text-align: center; }
  .notes-block { padding: 10px 12px; background: #f8fafc; border-left: 3px solid #14B8A6;
    border-radius: 4px; font-size: 12px; color: #475569; margin-top: 6px; }
  @media print { body { padding: 20px; } }
`;

function clinicHeaderHtml(doctor, docTitle) {
  const logo = doctor?.clinic_logo || "";
  const logoHtml = logo ? `<img src="${esc(logo)}" alt="Clinic logo" style="max-height:56px;max-width:180px;object-fit:contain;margin-bottom:8px;" />` : "";
  return `
    <div class="header">
      <div>
        ${logoHtml}
        <div class="clinic-name">${esc(doctor?.clinic_name || doctor?.name || "Clinic")}</div>
        ${doctor?.clinic_name ? `<div class="doctor-name">${esc(doctor?.name || "")}</div>` : ""}
        <div class="clinic-info">
          ${doctor?.clinic_address ? esc(doctor.clinic_address) + "<br/>" : ""}
          ${doctor?.phone ? "Phone: " + esc(doctor.phone) : ""}${doctor?.phone && doctor?.email ? " • " : ""}${doctor?.email ? esc(doctor.email) : ""}
        </div>
      </div>
      <div>
        <div class="doc-title">${esc(docTitle)}</div>
        <div class="doc-date">${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} • ${new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</div>
      </div>
    </div>`;
}

function footerNoteHtml(doctor) {
  const note = (doctor?.prescription_footer_note || "").trim();
  return note ? `<div class="notes-block" style="margin-top:24px;">${esc(note)}</div>` : "";
}

function patientBoxHtml(patient) {
  return `
    <div class="patient-box">
      <div class="patient-label">Patient</div>
      <div class="patient-name">${esc(patient?.name || "")}</div>
      <div class="patient-info">
        UHID: ${esc(patient?.uhid || "")}${patient?.phone ? " • Phone: " + esc(patient.phone) : ""}${patient?.age ? " • Age: " + esc(String(patient.age)) : ""}${patient?.gender ? " • " + esc(patient.gender) : ""}
      </div>
      ${patient?.address ? `<div class="patient-info">${esc(patient.address)}</div>` : ""}
    </div>`;
}

function openPrintable(html) {
  const win = window.open("", "_blank", "width=900,height=1000");
  if (!win) { alert("Please allow pop-ups to print."); return; }
  win.document.open();
  win.document.write(html);
  win.document.close();
}

// ============== EXAMINATION RECORD ==============
export function printExaminationRecord({ doctor, patient, teeth = [] }) {
  const rows = teeth
    .filter((t) => (t.conditions?.length || 0) > 0 || (t.notes || "").trim())
    .sort((a, b) => String(a.tooth).localeCompare(String(b.tooth)))
    .map((t) => `
      <tr>
        <td class="mono">${esc(t.tooth)}</td>
        <td>${(t.conditions || []).map((c) => `<span class="badge">${esc(c)}</span>`).join("")}</td>
        <td>${esc(t.notes || "")}</td>
      </tr>`)
    .join("");

  const body = rows
    ? `<table>
         <thead><tr><th style="width:80px">Tooth</th><th>Conditions</th><th>Notes</th></tr></thead>
         <tbody>${rows}</tbody>
       </table>`
    : `<p style="padding:20px;text-align:center;color:#64748b;font-size:13px;">No tooth conditions recorded yet.</p>`;

  const html = `
<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Examination Record</title>
<style>${BRAND_STYLES}</style></head><body>
${clinicHeaderHtml(doctor, "EXAMINATION")}
${patientBoxHtml(patient)}
<h2>Examination Findings (FDI Notation)</h2>
${body}
${footerNoteHtml(doctor)}
<div class="signature">
  <div></div>
  <div class="line">Doctor's Signature</div>
</div>
<div class="footer">Generated on ${new Date().toLocaleString("en-IN")} • DotMedy.com — Hi-Octane Practice</div>
<script>window.onload=function(){setTimeout(function(){window.print()},250);}<\/script>
</body></html>`;
  openPrintable(html);
}

// ============== LETTERHEAD (blank prescription page with clinic + patient details) ==============
export function printLetterhead({ doctor, patient, lastVisit = "" }) {
  const now = new Date();
  const printedAt = now.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
  });
  const html = `
<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Letterhead — ${esc(patient?.name || "")}</title>
<style>${BRAND_STYLES}
  @page { size: A4; margin: 12mm; }
  body { padding: 0 !important; font-size: 12px; }
  .header { margin-bottom: 8px !important; padding-bottom: 8px !important; }
  .header .clinic-name { font-size: 18px !important; margin-bottom: 2px !important; }
  .header .doctor-name { font-size: 12px !important; margin-bottom: 2px !important; }
  .header .clinic-info { font-size: 11px !important; line-height: 1.35 !important; }
  .header .doc-title { font-size: 20px !important; }
  .header .doc-date { font-size: 11px !important; }
  h2 { font-size: 13px !important; margin: 8px 0 4px 0 !important; padding-bottom: 3px !important; }
  .letter-patient { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 3px 14px; padding: 6px 10px;
    background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 4px; margin-bottom: 6px; font-size: 11px; line-height: 1.2; }
  .letter-patient .label { color: #64748b; font-size: 9px; text-transform: uppercase; letter-spacing: 0.4px; }
  .letter-patient .val { color: #0F2A5F; font-weight: 600; font-size: 11.5px; }
  .rx { min-height: 380px; padding: 6px 4px; }
  .fees-note { margin-top: 8px; padding: 5px 10px; background: #fef3c7; border-left: 3px solid #f59e0b;
    border-radius: 3px; font-size: 10.5px; color: #92400e; font-weight: 600; }
  .rx-hint { color: #cbd5e1; font-style: italic; font-size: 11px; }
  .signature { margin-top: 18px !important; font-size: 11px !important; }
  .signature .line { padding-top: 3px !important; width: 170px !important; }
  .notes-block { margin-top: 6px !important; padding: 5px 10px !important; font-size: 10.5px !important; }
  .footer { margin-top: 10px !important; padding-top: 6px !important; font-size: 9.5px !important; }
</style></head><body>
${clinicHeaderHtml(doctor, "PRESCRIPTION")}

<div class="letter-patient">
  <div><span class="label">Patient</span><br/><span class="val">${esc(patient?.name || "")}</span></div>
  <div><span class="label">UHID</span><br/><span class="val" style="font-family:monospace;">${esc(patient?.uhid || "")}</span></div>
  <div><span class="label">Phone</span><br/><span class="val">${esc(patient?.phone || "-")}</span></div>
  <div><span class="label">Age / Sex</span><br/><span class="val">${esc(patient?.age ? String(patient.age) : "-")} ${patient?.gender ? "• " + esc(patient.gender) : ""}</span></div>
  <div><span class="label">Last Visit</span><br/><span class="val">${lastVisit ? esc(lastVisit) : "First Visit"}</span></div>
  <div><span class="label">Printed On</span><br/><span class="val">${esc(printedAt)}</span></div>
  <div style="grid-column: 1 / -1;"><span class="label">Address</span><br/><span class="val">${esc(patient?.address || "-")}</span></div>
</div>

<h2>Rx</h2>
<div class="rx"><span class="rx-hint">Write prescription here …</span></div>

<div class="fees-note">*Fees paid is valid for 7 days only.</div>

${footerNoteHtml(doctor)}

<div class="signature">
  <div></div>
  <div class="line">Doctor's Signature</div>
</div>
<div class="footer">Generated on ${printedAt} • DotMedy.com — Hi-Octane Practice</div>
<script>window.onload=function(){setTimeout(function(){window.print()},250);}<\/script>
</body></html>`;
  openPrintable(html);
}
export function printTreatmentPlan({ doctor, patient, items = [] }) {
  const total = items.filter((a) => a.status !== "Cancelled").reduce((s, x) => s + Number(x.fee || 0), 0);
  const completed = items.filter((a) => a.status === "Completed").reduce((s, x) => s + Number(x.fee || 0), 0);
  const pending = items
    .filter((a) => a.status === "Advised" || a.status === "In Progress")
    .reduce((s, x) => s + Number(x.fee || 0), 0);

  const rows = items.map((a) => `
    <tr>
      <td>${esc(a.name)}${a.notes ? `<div style="color:#64748b;font-size:11px;margin-top:2px;">${esc(a.notes)}</div>` : ""}</td>
      <td class="mono">${a.tooth ? "#" + esc(String(a.tooth)) : "—"}</td>
      <td><span class="status-badge status-${(a.status || "advised").toLowerCase().replace(/\s+/g, "-")}">${esc(a.status)}</span></td>
      <td class="right">₹${Number(a.fee || 0).toLocaleString("en-IN")}</td>
    </tr>`).join("");

  const body = items.length
    ? `<table>
         <thead><tr><th>Treatment</th><th style="width:60px">Tooth</th><th style="width:110px">Status</th><th class="right" style="width:110px">Fee</th></tr></thead>
         <tbody>${rows}</tbody>
       </table>
       <div class="totals">
         <div class="row"><span>Completed</span><span>₹${completed.toLocaleString("en-IN")}</span></div>
         <div class="row"><span>Pending</span><span>₹${pending.toLocaleString("en-IN")}</span></div>
         <div class="row grand"><span>Total Plan</span><span>₹${total.toLocaleString("en-IN")}</span></div>
       </div>`
    : `<p style="padding:20px;text-align:center;color:#64748b;font-size:13px;">No treatments advised.</p>`;

  const html = `
<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Treatment Plan</title>
<style>${BRAND_STYLES}</style></head><body>
${clinicHeaderHtml(doctor, "TREATMENT PLAN")}
${patientBoxHtml(patient)}
<h2>Advised Treatments</h2>
${body}
${footerNoteHtml(doctor)}
<div class="signature">
  <div class="line">Patient's Signature</div>
  <div class="line">Doctor's Signature</div>
</div>
<div class="footer">Generated on ${new Date().toLocaleString("en-IN")} • DotMedy.com — Hi-Octane Practice</div>
<script>window.onload=function(){setTimeout(function(){window.print()},250);}<\/script>
</body></html>`;
  openPrintable(html);
}
